import React, { useState } from 'react';

interface Script {
  id: string;
  name: string;
  description: string;
  category: string;
  cis_control: string;
  version: string;
  is_active: boolean;
  is_builtin: boolean;
  tenant_id?: string | null;
  state?: 'global' | 'customized' | 'custom';
}

interface ScriptVersion {
  id: string;
  script_id: string;
  version_number: number;
  changed_by: string | null;
  changed_at: string;
  change_reason: string | null;
  is_current: boolean;
}

import * as apiService from '../../services/api';

// Group scripts by category
function groupByCategory(scripts: Script[]): Map<string, Script[]> {
  const groups = new Map<string, Script[]>();
  for (const s of scripts) {
    const existing = groups.get(s.category) || [];
    existing.push(s);
    groups.set(s.category, existing);
  }
  return groups;
}

const ScriptRepository: React.FC = () => {
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedScript, setSelectedScript] = useState<Script | null>(null);
  const [scriptContent, setScriptContent] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importForm, setImportForm] = useState({ name: '', description: '', category: 'Custom', content: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [versions, setVersions] = useState<ScriptVersion[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [resetConfirmId, setResetConfirmId] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const data = await apiService.getScripts();
      setScripts(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchScripts(); }, []);

  const grouped = groupByCategory(scripts);

  const selectScript = async (script: Script) => {
    setSelectedScript(script);
    setScriptContent(null);
    setContentLoading(true);
    try {
      const full = await apiService.getScript(script.id);
      setScriptContent(full.script_content || full.scriptContent || null);
    } catch {
      setScriptContent(null);
    } finally {
      setContentLoading(false);
    }
  };

  const toggleScript = async (scriptId: string) => {
    const script = scripts.find(s => s.id === scriptId);
    if (!script) return;
    const newState = !script.is_active;
    // Optimistic update
    setScripts(prev => prev.map(s => s.id === scriptId ? { ...s, is_active: newState } : s));
    try {
      await apiService.toggleScriptAssignment({ scriptId, targetType: 'all', isEnabled: newState });
    } catch {
      // Revert on failure
      setScripts(prev => prev.map(s => s.id === scriptId ? { ...s, is_active: !newState } : s));
    }
  };

  const handleImport = async () => {
    if (!importForm.name || !importForm.content) return;
    try {
      const newScript = await apiService.createScript({
        name: importForm.name,
        description: importForm.description,
        category: importForm.category || 'Custom',
        scriptContent: importForm.content,
      });
      setScripts(prev => [...prev, { ...newScript, is_active: true, is_builtin: false }]);
      setShowImport(false);
      setImportForm({ name: '', description: '', category: 'Custom', content: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import script');
    }
  };

  const handleDelete = async (scriptId: string) => {
    setDeleting(true);
    try {
      await apiService.deleteScript(scriptId);
      setScripts(prev => prev.filter(s => s.id !== scriptId));
      if (selectedScript?.id === scriptId) {
        setSelectedScript(null);
        setScriptContent(null);
      }
      setDeleteConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete script');
    } finally {
      setDeleting(false);
    }
  };

  const fetchVersions = async (scriptId: string) => {
    setVersionsLoading(true);
    setShowVersions(true);
    try {
      const data = await apiService.getScriptVersions(scriptId);
      setVersions(Array.isArray(data) ? data : []);
    } catch {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleReset = async (scriptId: string) => {
    setResetting(true);
    try {
      await apiService.resetScript(scriptId);
      setResetConfirmId(null);
      setSelectedScript(null);
      setScriptContent(null);
      setShowVersions(false);
      await fetchScripts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset script');
    } finally {
      setResetting(false);
    }
  };

  const getStateBadge = (script: Script) => {
    const state = script.state || (script.is_builtin ? 'global' : 'custom');
    switch (state) {
      case 'global':
        return <span className="text-[9px] px-1.5 py-0.5 rounded bg-anchor-blue/10 text-anchor-blue border border-anchor-blue/20">Built-in</span>;
      case 'customized':
        return <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">Customized</span>;
      case 'custom':
        return <span className="text-[9px] px-1.5 py-0.5 rounded bg-anchor-teal/10 text-anchor-teal border border-anchor-teal/20">Custom</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 text-xs text-amber-300 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchScripts} className="text-anchor-blue hover:text-white ml-2">Retry</button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue"></div>
        </div>
      ) : null}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-white/40">{scripts.length} scripts across {grouped.size} categories</p>
        </div>
        <button
          onClick={() => setShowImport(!showImport)}
          className="py-2 px-4 bg-anchor-blue border-none rounded-lg text-white text-sm cursor-pointer hover:bg-[#3636DD] transition-colors"
        >
          Import Script
        </button>
      </div>

      {/* Import form */}
      {showImport && (
        <div className="bg-white/[0.02] border border-anchor-blue/20 rounded-xl p-5 space-y-3">
          <h4 className="text-sm font-medium text-white">Import Custom Script</h4>
          <input type="text" placeholder="Script name" value={importForm.name} onChange={e => setImportForm(f => ({ ...f, name: e.target.value }))}
            className="w-full py-2 px-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50" />
          <input type="text" placeholder="Description" value={importForm.description} onChange={e => setImportForm(f => ({ ...f, description: e.target.value }))}
            className="w-full py-2 px-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50" />
          <select value={importForm.category} onChange={e => setImportForm(f => ({ ...f, category: e.target.value }))}
            className="w-full py-2 px-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white/70 focus:outline-none focus:border-anchor-blue/50">
            <option value="Custom">Custom</option>
            {Array.from(grouped.keys()).map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <textarea placeholder="Paste PowerShell script content..." value={importForm.content} onChange={e => setImportForm(f => ({ ...f, content: e.target.value }))} rows={6}
            className="w-full py-2 px-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white/70 font-mono placeholder-white/30 focus:outline-none focus:border-anchor-blue/50" />
          <div className="flex gap-2">
            <button onClick={handleImport} className="py-2 px-4 bg-anchor-blue rounded-lg text-white text-sm hover:bg-[#3636DD] transition-colors">Save</button>
            <button onClick={() => setShowImport(false)} className="py-2 px-4 bg-white/[0.03] border border-white/[0.06] rounded-lg text-white/50 text-sm hover:text-white/70 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Script tree */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: tree view */}
        <div className="lg:col-span-2 space-y-3">
          {Array.from(grouped.entries()).map(([category, categoryScripts]) => {
            const allEnabled = categoryScripts.every(s => s.is_active);
            const someEnabled = categoryScripts.some(s => s.is_active);
            const cisControl = categoryScripts[0]?.cis_control;

            return (
              <div key={category} className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
                {/* Category header */}
                <div className="flex items-center justify-between py-3 px-4 bg-white/[0.01] border-b border-white/[0.04]">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const newState = !allEnabled;
                        categoryScripts.forEach(s => {
                          setScripts(prev => prev.map(p => p.id === s.id ? { ...p, is_active: newState } : p));
                        });
                      }}
                      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        allEnabled ? 'bg-anchor-teal border-anchor-teal' :
                        someEnabled ? 'bg-anchor-blue/30 border-anchor-blue' :
                        'bg-transparent border-white/20'
                      }`}
                    >
                      {(allEnabled || someEnabled) && <span className="text-[10px] text-white">✓</span>}
                    </button>
                    <span className="text-[10px] text-anchor-blue font-mono">{cisControl}</span>
                    <span className="text-sm text-white/80">{category}</span>
                  </div>
                  <span className="text-[10px] text-white/30">{categoryScripts.filter(s => s.is_active).length}/{categoryScripts.length} active</span>
                </div>

                {/* Scripts in category */}
                {categoryScripts.map(script => (
                  <div
                    key={script.id}
                    className={`flex items-center justify-between py-2.5 px-4 pl-10 hover:bg-white/[0.02] cursor-pointer transition-colors border-b border-white/[0.02] last:border-b-0 ${
                      selectedScript?.id === script.id ? 'bg-white/[0.03]' : ''
                    }`}
                    onClick={() => selectScript(script)}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleScript(script.id); }}
                        className={`w-3.5 h-3.5 rounded border transition-colors ${
                          script.is_active ? 'bg-anchor-teal border-anchor-teal' : 'bg-transparent border-white/20'
                        }`}
                      >
                        {script.is_active && <span className="text-[8px] text-white block text-center">✓</span>}
                      </button>
                      <span className="text-xs text-white/70 font-mono">{script.name}.ps1</span>
                      {getStateBadge(script)}
                    </div>
                    <span className="text-[10px] text-white/30">v{script.version}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Right: detail panel */}
        <div>
          {selectedScript ? (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 sticky top-4">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-base text-white font-medium">{selectedScript.name}.ps1</h4>
                {getStateBadge(selectedScript)}
              </div>
              <p className="text-xs text-white/40 mb-4">{selectedScript.description}</p>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-xs">
                  <span className="text-white/40">Category</span>
                  <span className="text-white/70">{selectedScript.category}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-white/40">CIS Control</span>
                  <span className="text-anchor-blue">{selectedScript.cis_control || 'None'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-white/40">Version</span>
                  <span className="text-white/70">{selectedScript.version}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-white/40">Type</span>
                  <span className="text-white/70">{selectedScript.is_builtin ? 'Built-in' : 'Custom'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-white/40">Status</span>
                  <span className={selectedScript.is_active ? 'text-anchor-teal' : 'text-white/30'}>
                    {selectedScript.is_active ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => toggleScript(selectedScript.id)}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm transition-colors ${
                    selectedScript.is_active
                      ? 'bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20'
                      : 'bg-anchor-teal/10 border border-anchor-teal/20 text-anchor-teal hover:bg-anchor-teal/20'
                  }`}
                >
                  {selectedScript.is_active ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => fetchVersions(selectedScript.id)}
                  className="py-2 px-4 bg-white/[0.03] border border-white/[0.06] rounded-lg text-sm text-white/50 hover:text-white/70 transition-colors"
                >
                  History
                </button>
              </div>

              {/* Reset to Default button for customized scripts */}
              {selectedScript.state === 'customized' && (
                resetConfirmId === selectedScript.id ? (
                  <div className="mb-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                    <p className="text-xs text-amber-300 mb-2">Reset this script to the default built-in version? Your customizations will be lost.</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleReset(selectedScript.id)}
                        disabled={resetting}
                        className="flex-1 py-1.5 px-3 bg-amber-500/20 border border-amber-500/30 rounded-lg text-xs text-amber-400 hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                      >
                        {resetting ? 'Resetting...' : 'Confirm Reset'}
                      </button>
                      <button
                        onClick={() => setResetConfirmId(null)}
                        className="flex-1 py-1.5 px-3 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white/50 hover:text-white/70 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setResetConfirmId(selectedScript.id)}
                    className="w-full mb-2 py-2 px-4 bg-amber-500/5 border border-amber-500/10 rounded-lg text-sm text-amber-400/60 hover:bg-amber-500/10 hover:text-amber-400 transition-colors"
                  >
                    Reset to Default
                  </button>
                )
              )}

              {!selectedScript.is_builtin && (
                deleteConfirmId === selectedScript.id ? (
                  <div className="mb-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-xs text-red-300 mb-2">Are you sure you want to delete this script? This action cannot be undone.</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDelete(selectedScript.id)}
                        disabled={deleting}
                        className="flex-1 py-1.5 px-3 bg-red-500/20 border border-red-500/30 rounded-lg text-xs text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                      >
                        {deleting ? 'Deleting...' : 'Confirm Delete'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="flex-1 py-1.5 px-3 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white/50 hover:text-white/70 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirmId(selectedScript.id)}
                    className="w-full mb-2 py-2 px-4 bg-red-500/5 border border-red-500/10 rounded-lg text-sm text-red-400/60 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                  >
                    Delete Script
                  </button>
                )
              )}

              {/* Version history panel */}
              {showVersions && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-xs text-white/40 uppercase tracking-wider">Version History</h5>
                    <button onClick={() => setShowVersions(false)} className="text-[10px] text-white/30 hover:text-white/50">Close</button>
                  </div>
                  {versionsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-anchor-blue"></div>
                    </div>
                  ) : versions.length === 0 ? (
                    <p className="text-xs text-white/20 italic">No version history available</p>
                  ) : (
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {versions.map(v => (
                        <div key={v.id} className={`flex items-start justify-between py-1.5 px-2 rounded text-[11px] ${v.is_current ? 'bg-anchor-blue/5 border border-anchor-blue/10' : 'bg-white/[0.01]'}`}>
                          <div>
                            <span className="text-white/60 font-mono">v{v.version_number}</span>
                            {v.is_current && <span className="ml-1.5 text-[9px] text-anchor-blue">(current)</span>}
                            {v.change_reason && <p className="text-white/30 mt-0.5">{v.change_reason}</p>}
                          </div>
                          <div className="text-right text-white/25 shrink-0 ml-2">
                            <div>{new Date(v.changed_at).toLocaleDateString()}</div>
                            {v.changed_by && <div>{v.changed_by}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Script content preview */}
              <div className="mt-4">
                <h5 className="text-xs text-white/40 uppercase tracking-wider mb-2">Script Content</h5>
                {contentLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-anchor-blue"></div>
                  </div>
                ) : scriptContent ? (
                  <pre className="bg-black/30 border border-white/[0.06] rounded-lg p-3 text-[11px] text-green-300/80 font-mono overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre leading-relaxed">
                    {scriptContent}
                  </pre>
                ) : (
                  <p className="text-xs text-white/20 italic">No content available</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 text-center">
              <p className="text-xs text-white/30">Select a script to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScriptRepository;
